import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import type { QuestionTrend, SurveyWithinTimeFrame } from '../../../api/queries';
import { DateRangePicker, type DateRange } from '../../../components/DateRangePicker/DateRangePicker';
import { formatScore } from '../../../components/AnalysisLayout/analysis.utils';
import { summarizeTrends, trendTones } from '../dashboard.utils';
import { ScoreChart } from './ScoreChart';
import classes from '../TeamDashboard.module.css';

type TrendSectionProps = {
  dateRange: DateRange;
  setDateRange: Dispatch<SetStateAction<DateRange>>;
  /** Feilmelding når perioden ikke er gyldig */
  periodError?: string;
  surveys: { data?: SurveyWithinTimeFrame[]; isLoading: boolean; error: Error | null };
  questionTrends: { data?: QuestionTrend[]; isLoading: boolean; error: Error | null };
};

/** Snittet per måling og trendene for spørsmålene i perioden */
export const TrendSection = ({
  dateRange,
  setDateRange,
  periodError,
  surveys,
  questionTrends,
}: TrendSectionProps) => {
  const { t } = useTranslation();
  const summary = summarizeTrends(questionTrends.data ?? []);

  const chart = () => {
    if (periodError) {
      return (
        <Alert data-color="info" className={classes.chartCard}>
          <Paragraph>{periodError}</Paragraph>
        </Alert>
      );
    }
    if (surveys.isLoading) {
      return (
        <div aria-busy="true" className={classes.chartCard}>
          <Skeleton variant="rectangle" width="100%" height={260} />
        </div>
      );
    }
    if (surveys.error) {
      return (
        <Alert data-color="danger" className={classes.chartCard}>
          <Paragraph>{surveys.error.message}</Paragraph>
        </Alert>
      );
    }
    if (!surveys.data?.length) {
      return (
        <div className={`${classes.chartCard} ${classes.emptyCard}`}>
          <Heading level={3} data-size="2xs">
            {t('dashboard.chart.noData')}
          </Heading>
          <Paragraph data-size="sm" className={classes.subtle}>
            {t('dashboard.chart.noDataDescription')}
          </Paragraph>
        </div>
      );
    }
    return <ScoreChart data={surveys.data} />;
  };

  const trends = () => {
    if (questionTrends.isLoading) {
      return <Skeleton variant="rectangle" width="100%" height={160} />;
    }
    if (questionTrends.error) {
      return (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">{t('dashboard.questionTrends.couldNotLoad')}</Paragraph>
        </Alert>
      );
    }
    if (summary.questionCount === 0) {
      return (
        <Paragraph data-size="sm" className={classes.subtle}>
          {t('dashboard.trend.noTrends')}
        </Paragraph>
      );
    }
    return (
      <>
        <dl className={classes.miniStats}>
          <div className={classes.miniStat}>
            <dt>{t('dashboard.questionTrends.activeQuestions')}</dt>
            <dd className={classes.miniNumber}>{summary.questionCount}</dd>
          </div>
          {summary.average !== null && (
            <div className={classes.miniStat}>
              <dt>{t('dashboard.status.average')}</dt>
              <dd className={classes.statValue}>
                <span className={classes.miniNumber}>{formatScore(summary.average)}</span>
                <span className={classes.subtle}>{t('analysis.scoreOf')}</span>
              </dd>
            </div>
          )}
        </dl>
        <div className={classes.movers}>
          <Heading level={3} data-size="2xs">
            {t('dashboard.trend.trends')}
          </Heading>
          <ul className={classes.moverList}>
            {summary.top.map((item) => (
              <li key={item.id}>
                <Paragraph data-size="sm">
                  {item.question} ({formatScore(item.score, 2)})
                </Paragraph>
                <span className={classes.moverTrend} data-tone={trendTones[item.direction]}>
                  {t(`dashboard.trend.direction.${item.direction}`)}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <a href="#questions-heading" className="ds-link" data-size="sm">
          {t('dashboard.trend.seeAllQuestions')}
        </a>
      </>
    );
  };

  return (
    <section aria-labelledby="trend-heading" className={classes.section}>
      <div className={classes.sectionHeader}>
        <div className={classes.heading}>
          <Heading id="trend-heading" level={2} data-size="md">
            {t('dashboard.trend.title')}
          </Heading>
          <Paragraph className={classes.subtle}>{t('dashboard.trend.description')}</Paragraph>
        </div>
        <div className={classes.period}>
          <DateRangePicker
            names={{ from: 'fromDate', to: 'toDate' }}
            values={{ from: new Date(dateRange.fromDate), to: new Date(dateRange.toDate) }}
            setDateRange={setDateRange}
          />
        </div>
      </div>
      <div className={classes.trendColumns}>
        {chart()}
        <aside aria-label={t('dashboard.trend.asideLabel')} className={classes.trendAside}>
          {trends()}
        </aside>
      </div>
    </section>
  );
};
